/* eslint-disable react/no-unescaped-entities */
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import useNonInitialEffect from '@versiondos/hooks';
import { useApiClientWrapper } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPetServicesThunk, NewBookingsStatus } from '@spike/new-bookings-action';
import { PetService } from '@spike/new-booking-model';
import { PetCustomPrice } from '@spike/pet-model';
import { fetchPetCustomPricesThunk } from '@spike/pets-action';
import { RootState } from 'store';
import { Spinner } from 'components/UI';
import SelectServices from './SelectServices';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { AlertNewBooking } from '../UI/AlertNewBooking';
import clsx from 'clsx';
import { PetServiceWrapper } from 'model/NewMultipleBooking';
import { resolveStandardPrice } from '../utils/bookingCustomPrices';

interface Props {
    clientId: number;
    petId: number;
    selectedService?: PetServiceWrapper;
    readOnly?: boolean;
    withoutBorder?: boolean;
    servicesSelected?: Array<number>;
    multiplePets?: boolean;
    customizable?: boolean;
    isEvaluation?: boolean;
    onSelectService: (service: PetServiceWrapper, data: boolean) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            borderBottom: (props: Props) => (props.multiplePets ? 'none' : '1px solid #D4D4D4'),
            paddingBottom: 18,
            [theme.breakpoints.up('md')]: {
                paddingBottom: 20
            }
        },
        title: {
            fontWeight: 600,
            fontSize: 16,
            lineHeight: '16px',
            marginBottom: 18,
            [theme.breakpoints.up('md')]: {
                marginBottom: 20
            }
        },
        alert: {
            marginBottom: '0px !important',
            height: 53
        },
        alertText: {
            fontSize: 14,
            lineHeight: '20px'
        },
        withoutBorder: {
            border: 'none !important'
        }
    })
);

export const ServiceSelector: FunctionComponent<Props> = props => {
    const classes = useStyles(props);
    const dispatch = useDispatch();

    const ref = useRef<HTMLDivElement>(null);

    const apiClientWrapper = useApiClientWrapper();

    const petServices = useSelector<RootState, Array<PetService>>(state => state.newBookings.petServices);
    const customPrices = useSelector<RootState, Array<PetCustomPrice>>(state => state.pets.customPrices);
    const customPricesLoading = useSelector<RootState, boolean>(state => state.pets.customPricesLoading);

    const newBookingStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);

    const services: Array<PetServiceWrapper> = petServices
        .filter(petService => petService.businessArea === 'grooming' || petService.businessArea === 'dental')
        .map(petService => {
            const catalogPrice = petService.price;
            const resolved = resolveStandardPrice(catalogPrice, customPrices || [], petService.service.id);
            return {
                ...petService,
                duration: {
                    hours: Math.trunc(petService.durationInMinutes / 60),
                    minutes: petService.durationInMinutes % 60
                },
                beforePrice: catalogPrice,
                price: resolved.price,
                customPriceId: resolved.customPriceId
            };
        })
        .sort((wrapper, otherWrapper) =>
            wrapper.service.name.toLowerCase().localeCompare(otherWrapper.service.name.toLowerCase())
        );

    const [loading, setLoading] = useState(true);

    const baseServices: Array<PetServiceWrapper> =
        props.isEvaluation && props.selectedService
            ? [
                  {
                      ...props.selectedService,
                      duration: {
                          hours: Math.trunc(props.selectedService.durationInMinutes / 60),
                          minutes: props.selectedService.durationInMinutes % 60
                      },
                      beforePrice: props.selectedService.beforePrice ?? props.selectedService.price
                  }
              ]
            : services;

    const getServicesUnused = () => {
        if (props.servicesSelected && !props.isEvaluation) {
            const servicesToSelect: Array<PetServiceWrapper> = [];
            services.forEach(serv => {
                const serviceFounded = props.servicesSelected?.find(s => s === serv?.service.id);
                if (!serviceFounded) {
                    servicesToSelect.push(serv);
                }
            });
            return servicesToSelect;
        }
        return baseServices;
    };

    useEffect(() => {
        // Don't fetch pet services if this is an evaluation flow - evaluation service is already pre-loaded
        if (!props.isEvaluation) {
            setLoading(true);
            dispatch(fetchPetServicesThunk(apiClientWrapper, props.petId));
            dispatch(fetchPetCustomPricesThunk(apiClientWrapper, props.petId));
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.petId]);

    useEffect(() => {
        if (!props.readOnly) {
            setTimeout(() => {
                ref?.current?.scrollIntoView();
            }, 2000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, props.petId]);

    useNonInitialEffect(() => {
        switch (newBookingStatus) {
            case NewBookingsStatus.FetchPetServicesSuccess:
            case NewBookingsStatus.Error:
                if (!customPricesLoading) {
                    setLoading(false);
                }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newBookingStatus, customPricesLoading]);

    useEffect(() => {
        if (
            loading &&
            !customPricesLoading &&
            (newBookingStatus === NewBookingsStatus.FetchPetServicesSuccess ||
                newBookingStatus === NewBookingsStatus.Error)
        ) {
            setLoading(false);
        }
    }, [customPricesLoading, newBookingStatus, loading]);

    const emptyServicesComponent = (
        <Box id="booking_div_no_services_alert">
            <AlertNewBooking
                className={classes.alert}
                bWidth="2px"
                icon={faExclamationTriangle}
                iconSize="lg"
                iconColor="#EAB464"
                bgColor="#F8F5F1"
                bdColor="#BCB8AE"
            >
                <Typography className={classes.alertText}>
                    No services available for this pet's size or hair length
                </Typography>
            </AlertNewBooking>
        </Box>
    );

    return (
        <Box
            className={clsx(classes.container, {
                [classes.withoutBorder]: props.withoutBorder
            })}
        >
            {loading ? (
                <Spinner />
            ) : (
                <div ref={ref}>
                    <Typography className={classes.title}>{props.multiplePets ? 'Services' : 'Service'}</Typography>
                    {getServicesUnused()?.length === 0 ? (
                        emptyServicesComponent
                    ) : (
                        <SelectServices
                            customizable={props.customizable}
                            placeholder="Select a Service"
                            selectedOption={props.selectedService}
                            options={getServicesUnused()}
                            isEvaluation={props.isEvaluation}
                            petId={props.petId}
                            onSelect={(service: PetServiceWrapper, data: boolean) => {
                                props.onSelectService(service, data);
                            }}
                        />
                    )}
                </div>
            )}
        </Box>
    );
};

export default ServiceSelector;
